import { Text, useSettings, WarningDialog } from '@innovyze/stylovyze';
import {
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { useGlobalization } from '@Translations';
import { TFunc } from '@Translations/types';

import ResampleDialogDatePicker from './ResampleDialogDatePicker';
import { DateTime } from 'luxon';
import AdvancedOptions, { DEFAULT_QUERY_SIZE_LIMIT } from './AdvancedOptions';

export enum SampleMode {
	specificTimePeriod = 'specificTimePeriod',
	latestDay = 'latestDay',
	latestWeek = 'latestWeek',
	latestMonth = 'latestMonth',
	latestQuarter = 'latestQuarter',
	latestSixMonths = 'latestSixMonths',
	latestYear = 'latestYear',
}

type SampleModes = { label: string; value: SampleMode }[];

const sampleModes = (t: TFunc): SampleModes => [
	{ label: t('Specific Time Period'), value: SampleMode.specificTimePeriod },
	{ label: t('Latest Day'), value: SampleMode.latestDay },
	{ label: t('Latest Week'), value: SampleMode.latestWeek },
	{ label: t('Latest Month'), value: SampleMode.latestMonth },
	{ label: t('Latest Quarter'), value: SampleMode.latestQuarter },
	{ label: t('Latest Six Months'), value: SampleMode.latestSixMonths },
	{ label: t('Latest Year'), value: SampleMode.latestYear },
];

const RESAMPLE_DEFAULT_RANGE = { hours: 1 };
const SAMPLE_MODES_RANGES = new Map([
	[SampleMode.latestDay, { days: 1 }],
	[SampleMode.latestWeek, { days: 7 }],
	[SampleMode.latestMonth, { months: 1 }],
	[SampleMode.latestQuarter, { months: 3 }],
	[SampleMode.latestSixMonths, { months: 6 }],
	[SampleMode.latestYear, { years: 1 }],
]);

export interface ResampleFormData {
	sampleMode: SampleMode;
	startTime: string;
	endTime: string;
	deltaTimeSeconds?: number;
}

interface ResampleModalProps {
	jobsEnabled: boolean;
	open: boolean;
	onConfirm: (formData: ResampleFormData) => void;
	onCancel: () => void;
}

const ResampleDialog = (props: ResampleModalProps) => {
	const { t } = useGlobalization();
	const { jobsEnabled, open, onConfirm, onCancel } = props;
	const { companySettings } = useSettings();

	const endLuxon = DateTime.local({ zone: companySettings.timeZoneIANA });
	const startLuxon = endLuxon.minus(RESAMPLE_DEFAULT_RANGE);
	const [startTime, setStartTime] = useState(startLuxon);
	const [endTime, setEndTime] = useState(endLuxon);
	const [sampleMode, setSampleMode] = useState<SampleMode>(
		SampleMode.specificTimePeriod,
	);
	const [deltaTimeSeconds, setDeltaTimeSeconds] = useState<
		number | undefined
	>(DEFAULT_QUERY_SIZE_LIMIT);

	const { minutes = 0 } = endTime.diff(startTime, 'minutes').toObject();
	const isValidDateRange = minutes >= 1;

	useEffect(() => {
		setStartTime(startLuxon.setZone(companySettings.timeZoneIANA));
		setEndTime(endLuxon.setZone(companySettings.timeZoneIANA));
	}, [companySettings.timeZoneIANA]);

	const onSelectChange = (event: SelectChangeEvent<string>) => {
		const sampleMode = event.target.value as SampleMode;
		setSampleMode(sampleMode);

		const minusTime =
			SAMPLE_MODES_RANGES.get(sampleMode) || RESAMPLE_DEFAULT_RANGE;
		setStartTime(endLuxon.minus(minusTime));
		setEndTime(endLuxon);
	};

	const onTimeChanged = (): void => {
		if (sampleMode !== SampleMode.specificTimePeriod) {
			setSampleMode(SampleMode.specificTimePeriod);
		}
	};

	const notifyParent = () => {
		if (isValidDateRange) {
			onConfirm({
				sampleMode,
				startTime: startTime.set({ second: 0, millisecond: 0 }).toISO(),
				endTime: endTime.set({ second: 0, millisecond: 0 }).toISO(),
				deltaTimeSeconds,
			});
		}
	};

	const handleOnDialogClose = () => {
		setDeltaTimeSeconds(DEFAULT_QUERY_SIZE_LIMIT);
		onCancel();
	};

	const bodyText1 = t('Do you really want to resample the list of sensors?');
	const bodyText2 = t(
		'- This action will delete existing data for the selected sensors over the specified time period, and refetch it.',
	);
	const bodyText3 = t(
		'- This action will require to re-calculate any tools where these sensors are used.',
	);

	return (
		<WarningDialog
			title={t('Resample data')}
			open={open}
			confirmText={t('Resample')}
			cancelText={t('Cancel')}
			onConfirm={notifyParent}
			onCancel={handleOnDialogClose}>
			<Grid container spacing={4}>
				<Grid container spacing={4} item xs={12}>
					<Grid item xs={12}>
						<FormControl
							variant="outlined"
							margin="normal"
							fullWidth>
							<InputLabel>{t('Sample Mode')}</InputLabel>
							<Select
								name="sampleMode"
								value={sampleMode}
								onChange={onSelectChange}
								label={t('Sample Mode')}>
								{sampleModes(t).map(({ label, value }) => (
									<MenuItem key={value} value={value}>
										{label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12}>
						<ResampleDialogDatePicker
							errorMessages={{
								invalid: t(
									'Start time must be before end time',
								),
							}}
							isValid={isValidDateRange}
							label={t('Start Time')}
							value={startTime}
							setValue={timestamp => {
								setStartTime(timestamp);
								onTimeChanged();
							}}
							dataCy="resample-start-time"
							name="startTime"
						/>
					</Grid>
					<Grid item xs={12}>
						<ResampleDialogDatePicker
							errorMessages={{
								invalid: t('End time must be after start time'),
							}}
							isValid={isValidDateRange}
							label={t('End Time')}
							value={endTime}
							setValue={timestamp => {
								setEndTime(timestamp);
								onTimeChanged();
							}}
							dataCy="resample-end-time"
							name="endTime"
						/>
					</Grid>
				</Grid>
				<Grid container spacing={2} item xs={12}>
					<Grid item xs={12}>
						<Text variant="body1">{bodyText1}</Text>
					</Grid>
					<Grid item xs={12}>
						<Text variant="body1">{bodyText2}</Text>
					</Grid>
					<Grid item xs={12}>
						<Text variant="body1">{bodyText3}</Text>
					</Grid>
					{jobsEnabled && (
						<Grid item xs={12}>
							<AdvancedOptions
								deltaTimeSeconds={deltaTimeSeconds}
								onDeltaTimeSecondsChange={setDeltaTimeSeconds}
							/>
						</Grid>
					)}
				</Grid>
			</Grid>
		</WarningDialog>
	);
};

export default ResampleDialog;
